import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ISyncList } from '@/libs/types/sync';
import { Color, FontFamily, FontSize } from '@/styles/GlobalStyles';

import { mapInterviewStatus } from '../../helpers/interviewStatus';

interface InterviewChartProps {
  interviews: ISyncList[];
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  total: number;
  valid: number;
  notConsidered: number;
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Color.gray.white,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  title: {
    fontSize: FontSize.base,
    fontFamily: FontFamily.semiBold,
    color: Color.darkSlateGray_100,
    marginBottom: 16,
  },
  chartWrapper: {
    width: '100%',
    height: 300,
  },
  emptyContainer: {
    height: 300,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyText: {
    fontSize: FontSize.sm,
    fontFamily: FontFamily.regular,
    color: Color.gray[200],
  },
});

export function InterviewChart({ interviews }: InterviewChartProps) {
  // Only render on web
  if (Platform.OS !== 'web') {
    return null;
  }

  // Process data: group interviews by day and status
  const chartData = React.useMemo(() => {
    const dataByDate: Record<
      string,
      { total: number; valid: number; notConsidered: number }
    > = {};

    interviews.forEach((interview) => {
      const dateKey = format(new Date(interview.created_at), 'yyyy-MM-dd');

      if (!dataByDate[dateKey]) {
        dataByDate[dateKey] = { total: 0, valid: 0, notConsidered: 0 };
      }

      dataByDate[dateKey].total += 1;

      const mappedStatus = mapInterviewStatus(interview);
      if (mappedStatus === 'Válida') {
        dataByDate[dateKey].valid += 1;
      } else if (mappedStatus === 'Não Considerada') {
        dataByDate[dateKey].notConsidered += 1;
      }
    });

    // Convert to array and sort by date
    const sortedDates = Object.keys(dataByDate).sort();

    return sortedDates.map(
      (dateKey): ChartDataPoint => ({
        date: dateKey,
        displayDate: format(parseISO(dateKey), 'dd/MM', { locale: ptBR }),
        total: dataByDate[dateKey].total,
        valid: dataByDate[dateKey].valid,
        notConsidered: dataByDate[dateKey].notConsidered,
      }),
    );
  }, [interviews]);

  if (chartData.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Entrevistas por Dia</Text>
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>
            Nenhum dado disponível para o período selecionado
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Entrevistas por Dia</Text>
      <View style={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={Color.gray[100]} />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 12, fill: Color.gray[300] }}
              axisLine={{ stroke: Color.gray[100] }}
              tickLine={{ stroke: Color.gray[100] }}
            />
            <YAxis
              tick={{ fontSize: 12, fill: Color.gray[300] }}
              axisLine={{ stroke: Color.gray[100] }}
              tickLine={{ stroke: Color.gray[100] }}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: Color.gray.white,
                border: `1px solid ${Color.gray[100]}`,
                borderRadius: 8,
                fontFamily: FontFamily.regular,
                fontSize: FontSize.sm,
              }}
              labelStyle={{ color: Color.darkSlateGray_100, fontWeight: 600 }}
            />
            <Legend
              wrapperStyle={{
                fontFamily: FontFamily.regular,
                fontSize: FontSize.sm,
              }}
            />
            <Line
              type="monotone"
              dataKey="total"
              name="Total"
              stroke={Color.primary[300]}
              strokeWidth={2}
              dot={{ fill: Color.primary[300], r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="valid"
              name="Válida"
              stroke={Color.secondary.green}
              strokeWidth={2}
              dot={{ fill: Color.secondary.green, r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="notConsidered"
              name="Não Considerada"
              stroke={Color.secondary.red}
              strokeWidth={2}
              dot={{ fill: Color.secondary.red, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </View>
    </View>
  );
}
